import { NavigationBack } from "@/components";
import useGallery from "@/hooks/useGallery";
import useProfile from "@/hooks/useProfile";
import { GalleryCameraSearchSchema } from "@/schemas";
import CachedIcon from "@mui/icons-material/Cached";
import CircleIcon from "@mui/icons-material/Circle";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Link, createFileRoute, useRouterState } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import orderBy from "lodash/orderBy";
import { useEffect, useMemo, useRef, useState } from "react";
import { Camera } from "react-camera-pro";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/gallery/camera")({
	validateSearch: zodSearchValidator(GalleryCameraSearchSchema),
	component: RouteComponent,
});

const errorMessage = {
	noCameraAccessible: "No camera device accessible",
	permissionDenied: "Camera permission denied",
	switchCamera: "Switch camera",
} as const;

const aspectRatios = [
	{
		label: "3:4",
		value: 3 / 4,
	},
	{
		label: "9:16",
		value: 9 / 16,
	},
	{
		label: "1:1",
		value: 1 / 1,
	},
];

function RouteComponent() {
	const navigate = Route.useNavigate();
	const messageTag = Route.useSearch({
		select: (state) => state.tag,
	});
	const channelName = useRouterState({
		// @ts-expect-error
		select: (s) => s.location.state?.channelName ?? null,
	});
	const cameraRef = useRef<HTMLDivElement>(null);
	const { profile } = useProfile();
	const { onCaptureImage } = useGallery();
	const currentFileStorage = useMemo(() => {
		if (!profile?.storages) return null;
		const sortedStorages = orderBy(
			profile.storages,
			[(item) => new Date(item.created_at)],
			["desc"],
		);
		return sortedStorages[0];
	}, [profile?.storages]);
	const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo | null>(
		null,
	);
	const [deviceOptions, setDeviceOptions] = useState<MediaDeviceInfo[]>([]);
	const [selectedAspectRatio, setSelectedAspectRatio] = useState(
		aspectRatios[0],
	);

	useEffect(() => {
		(async () => {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = devices.filter((i) => i.kind === "videoinput");
			setDeviceOptions(videoDevices);
		})();
	}, []);

	const onClickSwitchCamera = () => {
		if (deviceOptions.length < 2) {
			return toast.info("No other camera device found", {
				position: "bottom-center",
			});
		}
		const targetDevice =
			deviceOptions.find((i) => i.deviceId !== selectedDevice?.deviceId) ??
			deviceOptions[0];
		return setSelectedDevice(targetDevice);
	};

	const handleCaptureImage = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		// @ts-expect-error
		const base64Image = cameraRef.current?.takePhoto();
		await onCaptureImage(base64Image);
		return navigate({
			replace: !!messageTag,
			to: "/dashboard/gallery/view",
			search: {
				tag: messageTag,
			},
			state: {
				channelName,
			},
		});
	};

	const videoSourceDeviceId = useMemo(
		() => selectedDevice?.deviceId ?? deviceOptions[0]?.deviceId,
		[selectedDevice, deviceOptions],
	);

	return (
		<>
			<div className="relative !p-0 bg-dark h-full overflow-hidden">
				<div className="absolute z-10 top-0 left-0 w-full">
					<div className="container">
						<NavigationBack />
					</div>
				</div>
				<div className="flex flex-col justify-center items-center w-full h-full max-h-dvh">
					<Camera
						ref={cameraRef}
						videoSourceDeviceId={videoSourceDeviceId}
						errorMessages={errorMessage}
						aspectRatio={selectedAspectRatio.value}
					/>
				</div>
				<div className="absolute bottom-0 left-0 w-full">
					<div className="relative mx-auto w-fit px-4 py-2 rounded-full bg-dark/65 -translate-y-4">
						<ToggleButtonGroup
							color="primary"
							value={selectedAspectRatio.label}
							size="small"
							exclusive
							onChange={(_, val) =>
								setSelectedAspectRatio(
									aspectRatios.find((i) => i.label === val) ?? aspectRatios[0],
								)
							}
							aria-label="Aspect Ratio"
							className="gap-2 !rounded-full [&_button.Mui-selected]:!border-none [&_button.Mui-selected]:!bg-primary [&_button.Mui-selected]:!text-white"
						>
							{aspectRatios.map((item) => (
								<ToggleButton
									key={item.label}
									value={item.label}
									aria-label={item.label}
									className="!bg-dark/40 !rounded-full !px-4 !py-2 !text-white"
								>
									{item.label}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</div>
					<div className="flex justify-between items-center container py-6 px-4 bg-dark/65">
						<Link
							to="/dashboard/gallery"
							className="flex-none relative w-12 h-12 rounded-md bg-white"
						>
							{currentFileStorage?.file_id ? (
								<img
									src={currentFileStorage?.file_id}
									alt="captured"
									className="w-full h-full object-cover object-center"
									loading="eager"
								/>
							) : null}
						</Link>
						<IconButton
							color="primary"
							size="large"
							title="Capture"
							className="!border-2 !border-white !border-solid !p-1"
							onClick={handleCaptureImage}
						>
							<CircleIcon className="!text-white !w-14 !h-14" />
						</IconButton>
						<IconButton
							color="primary"
							size="large"
							title="Switch Camera"
							className="!border-2 !border-white !bg-dark/65"
							onClick={onClickSwitchCamera}
						>
							<CachedIcon className="!text-white" />
						</IconButton>
					</div>
				</div>
			</div>
		</>
	);
}
